import { Checkbox } from '@material-ui/core';
import CSS from 'csstype';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { unstable_batchedUpdates } from 'react-dom';

import { Calendar } from '~/src/dash/components/calendar/calendar';
import { usePanel } from '~/src/dash/components/layout/contexts';
import { MaterialSearchDropdownMulti } from '~/src/dash/components/material-search-dropdown-multi';
import { ITableGroupBy, ITableMetric } from '~/src/dash/components/tables/common';
import { Col, Cols, Row, Rows, spacing, Text, TextButton } from '~/src/dash/publicaUI';
import { IStore } from '~/src/dash/reducers/store';
import { IMenuList } from '~/src/dash/types/menuList';
import {
  generateCalendarQuickSelectorCustomOptions,
  generateCalendarQuickSelectorOptions,
  generateCompareQuickSelectorCustomOptions,
  generateCompareQuickSelectorOptions,
  IDualCalendarProps,
} from '~/src/dash/utils/calendar';
import { formatDateFromUTCToOffset, formatWithoutHoursMins } from '~/src/dash/utils/date';
import { FilterPill } from '~/src/dash/views/brands-dashboard/BrandsDashboard/BrandsDashboardFilters/FilterPill';
import {
  IBrandsDashboardDynamicFilter,
  IBrandsDashboardFilter,
} from '~/src/dash/views/brands-dashboard/BrandsDashboard/common/types';
import { useBrandsDashboardStore } from '~/src/dash/views/brands-dashboard/contexts';
import { ISingleSelectOption, SingleSelect } from '~/src/dash/views/singleselect-view';

interface IFilterLabelProps {
  svgHeight?: number;
  svgPath: string;
  svgStyle?: CSS.Properties;
  title: string;
  titleStyle?: CSS.Properties;
}

const FilterLabel = ({
  svgHeight,
  svgPath,
  svgStyle = {},
  title,
  titleStyle = {},
}: IFilterLabelProps) => {
  return (
    <Cols style={{ alignItems: 'center' }}>
      <Col style={{ ...svgStyle }}>
        <img src={svgPath} height={svgHeight ? `${svgHeight}px` : 'inherit'} />
      </Col>
      <Col
        style={{
          paddingLeft: spacing.small,
          paddingRight: spacing.medium,
          ...titleStyle,
        }}
      >
        <Text small bold>
          {title}
        </Text>
      </Col>
    </Cols>
  );
};

interface IBrandsDashboardFiltersProps extends IDualCalendarProps, IStore {
  timezone: string;
  timezonesList: IMenuList;
  handleTimezoneOnChange: (list: IMenuList) => void;
  filters: IBrandsDashboardFilter[];
  isClearFiltersEnabled: boolean;
  groupBySelectItems: ISingleSelectOption[];
  currentGroupBy: ITableGroupBy;
  handleGroupByOnChange: (name: string, value: string | string[] | number) => void;
  orderBySelectItems: ISingleSelectOption[];
  currentOrderBy: ITableMetric;
  handleOrderByOnChange: (name: string, value: string | string[] | number) => void;
  handleFilterAccept: (menuTitle: string, list: IMenuList) => void;
  handleClearFilters: () => void;
  handleDateRangeChange: (
    start: moment.Moment | null,
    end: moment.Moment | null,
    isCompareCalendar: boolean,
  ) => void;
}

export const BrandsDashboardFilters = ({
  user,
  session,
  timezone,
  timezonesList,
  handleTimezoneOnChange,
  // calendar props
  range1Selected,
  range1DisplaySelected,
  range2Selected,
  range2DisplaySelected,
  compStartDate,
  compEndDate,
  compDisplayStartDate,
  compDisplayEndDate,
  calStartDate,
  calEndDate,
  calDisplayStartDate,
  calDisplayEndDate,
  calFocusedInput,
  compFocusedInput,
  isComparedToEnabled,
  // calendar functions
  handleChangeAggRangeSelected,
  handleCalendarDateChange,
  handleDateRangeChange,
  handleCalendarFocusedInputChange,
  handleCompareEnabled,
  // filter props
  filters,
  isClearFiltersEnabled,
  groupBySelectItems,
  currentGroupBy,
  orderBySelectItems,
  currentOrderBy,
  handleGroupByOnChange,
  handleOrderByOnChange,
  handleFilterAccept,
  handleClearFilters,
}: IBrandsDashboardFiltersProps) => {
  const { cfg } = useBrandsDashboardStore();
  const { dynamicFilters, calendar } = cfg;
  const { colors } = usePanel();

  const [filtersLoadState, setFiltersLoadState] = useState(false);
  const [filtersState, setFiltersState] = useState<Record<string, IMenuList>>({});
  const [activateFilterClear, setActivateFilterClear] = useState({ clear: false });

  // on load, we run all api calls / functions to get dynamic filters populated
  useEffect(() => {
    let tempState = {} as Record<string, IMenuList>;
    const apiCalls = [] as Array<Promise<Record<string, IMenuList>>>;
    Object.values(dynamicFilters).forEach((filter: IBrandsDashboardDynamicFilter) => {
      const { getList } = filter;
      apiCalls.push(getList(session, user));
    });
    Promise.all(apiCalls).then((filters) => {
      for (const filter of filters) {
        tempState = { ...tempState, ...filter };
      }
      unstable_batchedUpdates(() => {
        setFiltersState(tempState);
        setFiltersLoadState(true);
      });
    });
  }, []);

  const handleClearFilterPills = () => {
    setActivateFilterClear({ clear: true });
    handleClearFilters();
  };

  // calendar consts
  const compareQuickSelectorOptions = generateCompareQuickSelectorOptions({
    timezone,
    startDate: calDisplayStartDate,
    endDate: calDisplayEndDate,
    rangeSelected: range1DisplaySelected,
  });
  const compareQuickSelectorCustomOptions = generateCompareQuickSelectorCustomOptions({
    timezone,
    startDate: calDisplayStartDate,
    endDate: calDisplayEndDate,
  });
  const isCalDayHighlighted = (day: moment.Moment): boolean => {
    if (!isComparedToEnabled) {
      return false;
    }
    return formatDateFromUTCToOffset(day.format('YYYY-MM-DDTHH:mm:ss'), 'UTC').isBetween(
      compDisplayStartDate,
      compDisplayEndDate,
      'day',
      '[]',
    );
  };
  const isCompDayHighlighted = (day: moment.Moment): boolean => {
    return formatDateFromUTCToOffset(day.format('YYYY-MM-DDTHH:mm:ss'), 'UTC').isBetween(
      calDisplayStartDate,
      calDisplayEndDate,
      'day',
      '[]',
    );
  };

  // consts needed for styling
  const filterLabelBlockWidth = 80;
  const filterRowMinHeight = '42px';

  return (
    <Rows>
      {/* CALENDAR */}
      <Row
        displayFlex
        style={{
          minHeight: filterRowMinHeight,
          borderBottom: `1px solid ${colors.lightGray}`,
          padding: '2px 16px',
        }}
      >
        <Cols
          max
          style={{
            position: 'relative',
            alignItems: 'flex-start',
          }}
        >
          <Col width={filterLabelBlockWidth} paddingTop={spacing.small}>
            <FilterLabel
              svgPath='/assets/shared/svg/Calendar15px.svg'
              title='Period'
              svgStyle={{ paddingTop: spacing.extraSmall }}
            />
          </Col>
          <Col>
            <Cols
              wrap
              style={{ alignItems: 'center' }}
              responsive
              largeStyle={{ justifyContent: 'flex-end' }}
            >
              {/* MAIN CALENDAR */}
              <Col>
                <Cols
                  wrap
                  style={{
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                  }}
                >
                  <Col style={{ margin: '2px 0' }}>
                    <Cols style={{ alignItems: 'center' }}>
                      <Col>
                        <Text extraSmall>{range1DisplaySelected}</Text>
                      </Col>
                      <Col style={{ paddingLeft: spacing.small, margin: '2px 0' }}>
                        <Calendar
                          startDate={calStartDate}
                          endDate={calEndDate}
                          calStartDate={calDisplayStartDate}
                          calEndDate={calDisplayEndDate}
                          startDateID='startDate'
                          endDateID='endDate'
                          rangeSelected={range1Selected}
                          handleRangeSelected={handleChangeAggRangeSelected}
                          handleCalendarDateChange={handleCalendarDateChange(false)}
                          handleDateRangeChange={handleDateRangeChange}
                          quickSelectorOptions={generateCalendarQuickSelectorOptions(timezone)}
                          quickSelectorCustomOptions={generateCalendarQuickSelectorCustomOptions(
                            timezone,
                          )}
                          isDayHighlighted={isCalDayHighlighted}
                          isCompareCalendar={false}
                          focusedInput={calFocusedInput || null}
                          excludeToday={true}
                          minDate={calendar.minDate}
                          dateFormat={formatWithoutHoursMins(session.publisher.Localization)}
                          handleFocusedInputChange={handleCalendarFocusedInputChange(false)}
                          trackOnClose={({
                            startDate,
                            endDate,
                          }: {
                            startDate: moment.Moment | null;
                            endDate: moment.Moment | null;
                          }): any => {
                            handleCalendarFocusedInputChange(true);
                            handleDateRangeChange(startDate, endDate, false);
                          }}
                          timezone={timezone}
                          anchorDirection='left'
                        />
                      </Col>
                    </Cols>
                  </Col>
                  {/* COMPARE CALENDAR */}
                  <Col paddingLeft={spacing.medium} style={{ margin: '2px 0' }}>
                    <Cols style={{ alignItems: 'center' }}>
                      <Col>
                        <Checkbox
                          size='small'
                          checked={isComparedToEnabled}
                          onChange={handleCompareEnabled}
                        />
                      </Col>
                      <Col>
                        <Text extraSmall>{range2DisplaySelected}</Text>
                      </Col>
                      <Col style={{ paddingLeft: spacing.small }}>
                        <Calendar
                          startDate={compStartDate}
                          endDate={compEndDate}
                          calStartDate={compDisplayStartDate}
                          calEndDate={compDisplayEndDate}
                          startDateID='startDate'
                          endDateID='endDate'
                          rangeSelected={range2Selected}
                          handleRangeSelected={handleChangeAggRangeSelected}
                          handleCalendarDateChange={handleCalendarDateChange(true)}
                          handleDateRangeChange={handleDateRangeChange}
                          quickSelectorOptions={compareQuickSelectorOptions}
                          quickSelectorCustomOptions={compareQuickSelectorCustomOptions}
                          isDayHighlighted={isCompDayHighlighted}
                          isCompareCalendar={true}
                          focusedInput={compFocusedInput || null}
                          minDate={calendar.minDate}
                          dateFormat={formatWithoutHoursMins(session.publisher.Localization)}
                          handleFocusedInputChange={handleCalendarFocusedInputChange(true)}
                          trackOnClose={({
                            startDate,
                            endDate,
                          }: {
                            startDate: moment.Moment | null;
                            endDate: moment.Moment | null;
                          }): any => {
                            handleCalendarFocusedInputChange(true);
                            handleDateRangeChange(startDate, endDate, false);
                          }}
                          timezone={timezone}
                          anchorDirection='left'
                        />
                      </Col>
                    </Cols>
                  </Col>
                </Cols>
              </Col>
              {/* TIMEZONES */}
              <Col style={{ margin: '2px 0px 0px 8px' }}>
                <Cols
                  style={{
                    alignItems: 'center',
                    marginBottom: '2px',
                  }}
                >
                  <Col paddingTop='4px'>
                    <img height='16px' src='/assets/shared/svg/clock.svg' />
                  </Col>
                  <Col style={{ paddingLeft: spacing.small }}>
                    <Text extraSmall>Timezone</Text>
                  </Col>
                  <Col style={{ paddingLeft: spacing.small }}>
                    <MaterialSearchDropdownMulti
                      fontSize='12px'
                      menuTitle='Timezone'
                      list={timezonesList}
                      onChange={handleTimezoneOnChange}
                      leftRightDropdownEnabled={true}
                      enableFixed={true}
                      selectLimit={1}
                      enableMultiSelect={false}
                    />
                  </Col>
                </Cols>
              </Col>
            </Cols>
          </Col>
        </Cols>
      </Row>
      {/* PILLS */}
      <Row
        displayFlex
        style={{
          minHeight: filterRowMinHeight,
          borderBottom: `1px solid ${colors.lightGray}`,
          padding: '4px 16px 2px',
        }}
      >
        <Cols
          max
          style={{
            position: 'relative',
            alignItems: 'flex-start',
            paddingBottom: '2px',
          }}
        >
          <Col width={filterLabelBlockWidth} paddingTop='5px'>
            <FilterLabel svgPath='/assets/shared/svg/Filter15px.svg' title='Filters' />
          </Col>
          <Col>
            {/* FILTER PILLS */}
            <Cols wrap style={{ alignItems: 'center' }}>
              {/* SET DYNAMIC FILTERS */}
              {filtersLoadState &&
                Object.keys(filtersState).map((filterKey) => {
                  const filter = dynamicFilters[filterKey];
                  return (
                    <Col>
                      <FilterPill
                        menuTitle={filter.menuTitle}
                        singularTitle={filter.singularString}
                        list={filtersState[filterKey]}
                        handleFilterAccept={handleFilterAccept}
                        activateFilterClear={activateFilterClear}
                      />
                    </Col>
                  );
                })}
              {/* SET STATIC FILTERS */}
              {filters.map((filter) => (
                <Col>
                  <FilterPill
                    menuTitle={filter.menuTitle}
                    singularTitle={filter.singularString || ''}
                    list={filter.list}
                    handleFilterAccept={handleFilterAccept}
                    activateFilterClear={activateFilterClear}
                    sortMode={filter.sortMode}
                  />
                </Col>
              ))}
              {/* CLEAR BUTTON */}
              {isClearFiltersEnabled && (
                <Col style={{ padding: '0px 8px' }}>
                  <TextButton size='extraSmall' onClick={handleClearFilterPills}>
                    Clear
                  </TextButton>
                </Col>
              )}
            </Cols>
          </Col>
        </Cols>
      </Row>
      {/* GROUP BYS */}
      <Row
        style={{
          minHeight: filterRowMinHeight,
          padding: '2px 16px',
        }}
      >
        <Cols
          max
          style={{
            position: 'relative',
            alignItems: 'flex-start',
          }}
        >
          <Col width={filterLabelBlockWidth} paddingTop='4px'>
            <FilterLabel
              svgPath='/assets/shared/svg/Data15px.svg'
              svgStyle={{ marginTop: spacing.extraSmall }}
              title='Data'
            />
          </Col>
          <Col>
            <Cols wrap>
              <Col>
                <Cols style={{ alignItems: 'center' }}>
                  <Col
                    style={{
                      paddingTop: '5px',
                      paddingRight: spacing.medium,
                    }}
                    responsive
                    mediumStyle={{ minWidth: '80px' }}
                  >
                    <Text extraSmall>Top 100</Text>
                  </Col>
                  <Col style={{ paddingRight: spacing.medium }}>
                    <SingleSelect
                      name='View By'
                      label=''
                      items={groupBySelectItems}
                      value={currentGroupBy.label}
                      onChange={handleGroupByOnChange}
                      style={{
                        fontSize: '12px',
                        marginTop: '4px',
                        marginBottom: '2px',
                        padding: '0px',
                        minWidth: '120px',
                      }}
                    />
                  </Col>
                </Cols>
              </Col>
              <Col>
                <Cols style={{ alignItems: 'center' }}>
                  <Col
                    style={{
                      paddingTop: '5px',
                      paddingRight: spacing.medium,
                    }}
                    responsive
                    mediumStyle={{ minWidth: '80px' }}
                  >
                    <Text extraSmall>Ordered By</Text>
                  </Col>
                  <Col>
                    <SingleSelect
                      name='Order By'
                      label=''
                      items={orderBySelectItems}
                      value={currentOrderBy.label}
                      onChange={handleOrderByOnChange}
                      style={{
                        fontSize: '12px',
                        marginTop: '4px',
                        marginBottom: '2px',
                        padding: '0px',
                        minWidth: '120px',
                      }}
                    />
                  </Col>
                </Cols>
              </Col>
            </Cols>
          </Col>
        </Cols>
      </Row>
    </Rows>
  );
};
